import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Settings, 
  Calendar,
  Link2,
  Unlink,
  RefreshCw,
  CheckCircle,
  XCircle,
  ExternalLink,
  Mail,
  Shield,
  User,
  Bell,
  Video
} from 'lucide-react';

// Google Calendar Icon
const GoogleCalendarIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <rect width="18" height="18" x="3" y="3" fill="#fff" rx="2"/>
    <path fill="#EA4335" d="M18 3H6a3 3 0 00-3 3v2h18V6a3 3 0 00-3-3z"/>
    <path fill="#4285F4" d="M21 8H3v10a3 3 0 003 3h12a3 3 0 003-3V8z"/>
    <path fill="#FBBC04" d="M9 12h6v6H9z"/>
    <path fill="#34A853" d="M15 12h3v6h-3z"/>
    <path fill="#EA4335" d="M6 12h3v6H6z"/>
  </svg>
);

// Microsoft Outlook Icon
const OutlookIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path fill="#0078D4" d="M24 7.5v9c0 .3-.1.5-.3.7-.2.2-.4.3-.7.3h-8.5v-11H23c.3 0 .5.1.7.3.2.2.3.4.3.7z"/>
    <path fill="#0364B8" d="M14.5 6.5v11H1c-.3 0-.5-.1-.7-.3-.2-.2-.3-.4-.3-.7v-9c0-.3.1-.5.3-.7.2-.2.4-.3.7-.3h13.5z"/>
    <path fill="#28A8EA" d="M14.5 6.5v5.5H0v-5.5h14.5z"/>
    <path fill="#50D9FF" d="M7 18l-7-6h14.5v6H7z"/>
    <circle cx="7" cy="12" r="4" fill="#fff"/>
    <path fill="#0078D4" d="M7 9c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3z"/>
  </svg>
);

export default function SettingsPage() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [calendarStatus, setCalendarStatus] = useState({
    google: { connected: false, configured: false, email: null },
    microsoft: { connected: false, configured: false, email: null }
  });
  const [syncing, setSyncing] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [activeTab, setActiveTab] = useState('calendar');

  useEffect(() => {
    loadCalendarStatus();
    
    // Check for connection result from OAuth callback
    const calendarConnected = searchParams.get('calendar_connected');
    const calendarError = searchParams.get('calendar_error');
    
    if (calendarConnected) {
      toast.success(`${calendarConnected === 'google' ? 'Google' : 'Microsoft'} Calendar conectado com sucesso!`);
      window.history.replaceState({}, '', '/settings');
    }
    
    if (calendarError) {
      toast.error('Erro ao conectar calendário. Tente novamente.');
      window.history.replaceState({}, '', '/settings');
    }
  }, [searchParams]);

  const loadCalendarStatus = async () => {
    try {
      const response = await api().get('/calendar/status');
      setCalendarStatus(response.data);
      
      if (response.data.google.connected || response.data.microsoft.connected) {
        loadCalendarEvents();
      }
    } catch (error) {
      console.error('Error loading calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await api().get('/calendar/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoadingEvents(false);
    }
  };

  const connectCalendar = async (provider) => {
    try {
      const response = await api().get(`/calendar/${provider}/auth`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar conexão');
    }
  };

  const disconnectCalendar = async (provider) => {
    if (!window.confirm(`Tem a certeza que deseja desconectar o ${provider === 'google' ? 'Google' : 'Microsoft'} Calendar?`)) {
      return;
    }
    
    try {
      await api().delete(`/calendar/${provider}/disconnect`);
      toast.success('Calendário desconectado');
      loadCalendarStatus();
    } catch (error) {
      toast.error('Erro ao desconectar calendário');
    }
  };

  const syncCalendar = async (provider) => {
    setSyncing(true);
    try {
      const response = await api().post(`/calendar/sync?provider=${provider}`, {
        sync_tasks: true,
        sync_routes: true
      });
      toast.success(response.data.message);
      loadCalendarEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-amber-500" />
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Definições</h1>
          <p className="text-muted-foreground">Gerir integrações e preferências</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Calendário
          </TabsTrigger>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Perfil
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notificações
          </TabsTrigger>
        </TabsList>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-500" />
                Integrações de Calendário
              </CardTitle>
              <CardDescription>
                Conecte o seu calendário para sincronizar tarefas e rotas automaticamente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Calendar */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-white border rounded-lg shadow-sm">
                    <GoogleCalendarIcon />
                  </div>
                  <div>
                    <h3 className="font-medium">Google Calendar</h3>
                    {calendarStatus.google.connected ? (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-emerald-500" />
                        Conectado: {calendarStatus.google.email}
                      </p>
                    ) : calendarStatus.google.configured ? (
                      <p className="text-sm text-muted-foreground">Pronto para conectar</p>
                    ) : (
                      <p className="text-sm text-amber-600">Aguarda configuração de API keys</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {calendarStatus.google.connected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => syncCalendar('google')}
                        disabled={syncing}
                        data-testid="sync-google-btn"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                        Sincronizar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => disconnectCalendar('google')}
                        data-testid="disconnect-google-btn"
                      >
                        <Unlink className="h-4 w-4 mr-1" />
                        Desconectar
                      </Button>
                    </>
                  ) : calendarStatus.google.configured ? (
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                      onClick={() => connectCalendar('google')}
                      data-testid="connect-google-btn"
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Conectar
                    </Button>
                  ) : (
                    <Badge variant="outline" className="text-amber-600">
                      Configuração necessária
                    </Badge>
                  )}
                </div>
              </div>

              {/* Microsoft Outlook */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-white border rounded-lg shadow-sm">
                    <OutlookIcon />
                  </div>
                  <div>
                    <h3 className="font-medium">Microsoft Outlook</h3>
                    {calendarStatus.microsoft.connected ? (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-emerald-500" />
                        Conectado: {calendarStatus.microsoft.email}
                      </p>
                    ) : calendarStatus.microsoft.configured ? (
                      <p className="text-sm text-muted-foreground">Pronto para conectar</p>
                    ) : (
                      <p className="text-sm text-amber-600">Aguarda configuração de API keys</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {calendarStatus.microsoft.connected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => syncCalendar('microsoft')}
                        disabled={syncing}
                        data-testid="sync-microsoft-btn"
                      >
                        <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                        Sincronizar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => disconnectCalendar('microsoft')}
                        data-testid="disconnect-microsoft-btn"
                      >
                        <Unlink className="h-4 w-4 mr-1" />
                        Desconectar
                      </Button>
                    </>
                  ) : calendarStatus.microsoft.configured ? (
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                      onClick={() => connectCalendar('microsoft')}
                      data-testid="connect-microsoft-btn"
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Conectar
                    </Button>
                  ) : (
                    <Badge variant="outline" className="text-amber-600">
                      Configuração necessária
                    </Badge>
                  )}
                </div>
              </div>

              {/* Info box */}
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-medium text-sm mb-2">O que será sincronizado:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Tarefas pendentes com data de vencimento
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Rotas planeadas (visitas a clientes)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    Reuniões com clientes
                  </li>
                </ul>
              </div>

              {/* Setup Instructions */}
              {(!calendarStatus.google.configured && !calendarStatus.microsoft.configured) && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Como configurar:</h4>
                  <div className="text-sm text-amber-700 space-y-2">
                    <p><strong>Google Calendar:</strong></p>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Aceda à <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                      <li>Crie um projeto e ative a Calendar API</li>
                      <li>Configure as credenciais OAuth 2.0</li>
                      <li>Adicione GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET ao ficheiro .env</li>
                    </ol>
                    <p className="mt-3"><strong>Microsoft Outlook:</strong></p>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Aceda ao <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="underline">Azure Portal</a></li>
                      <li>Registe uma aplicação no Azure AD</li>
                      <li>Adicione permissões Calendars.ReadWrite</li>
                      <li>Adicione MICROSOFT_CLIENT_ID e MICROSOFT_CLIENT_SECRET ao ficheiro .env</li>
                    </ol>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Calendar Events Preview */}
          {(calendarStatus.google.connected || calendarStatus.microsoft.connected) && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Próximos Eventos</CardTitle>
                  <Button variant="outline" size="sm" onClick={loadCalendarEvents} disabled={loadingEvents}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${loadingEvents ? 'animate-spin' : ''}`} />
                    Atualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingEvents ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
                  </div>
                ) : events.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Sem eventos nos próximos 30 dias
                  </p>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {events.slice(0, 10).map((event, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded ${event.provider === 'google' ? 'bg-red-100' : 'bg-blue-100'}`}>
                            {event.provider === 'google' ? <GoogleCalendarIcon /> : <OutlookIcon />}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{event.title}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(event.start_time).toLocaleDateString('pt-PT', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        {event.location && (
                          <Badge variant="outline" className="text-xs">
                            {event.location}
                          </Badge>
                        )}
                      </div>
                    ))}
                    {events.length > 10 && (
                      <p className="text-center text-sm text-muted-foreground pt-2">
                        +{events.length - 10} mais eventos
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5 text-amber-500" />
                Perfil do Utilizador
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-amber-600">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-medium text-lg">{user?.name}</h3>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <Badge className="mt-1 bg-amber-100 text-amber-800">
                    {user?.role === 'admin' ? 'Administrador' : user?.role === 'gestor' ? 'Gestor' : 'Vendedor'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-amber-500" />
                Preferências de Notificações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Alertas de Proximidade GPS</Label>
                  <p className="text-sm text-muted-foreground">Receber alertas quando estiver próximo de um cliente</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Lembretes de Tarefas</Label>
                  <p className="text-sm text-muted-foreground">Notificações para tarefas com prazo a vencer</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Resumo Semanal</Label>
                  <p className="text-sm text-muted-foreground">Receber email com resumo de atividades</p>
                </div>
                <Switch />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
